import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, throwError } from 'rxjs'
import { catchError, map, shareReplay } from 'rxjs/operators'
import {
  RowDetailEntry,
  RowDetailResponse,
  RowDetailSource,
} from '../models/row-detail.model'
import { RowDetailService } from './row-detail.service'

/**
 * With `<base href="/helios/">` this resolves to
 * `/helios/helios-flex-positions/flex-positions/row-detail`, which the committed
 * `helios-flex-positions` rule in proxy.conf.beta.json already routes onto
 * `https://helios-flex-positions.uswe2.beta.aws.cwan.io/api/flex-positions/row-detail`. No proxy
 * change is needed for this endpoint, and none may be committed.
 *
 * Same shape and the same two load-bearing `flex-positions` segments as
 * {@link FIELD_CATALOG_URL} — see that constant for why neither is a typo. The CIAM bearer token
 * and the `helios-session-id` header are both attached globally, by AuthTokenInterceptor and
 * SessionInterceptor; never set either by hand here.
 */
export const ROW_DETAIL_URL = 'helios-flex-positions/flex-positions/row-detail'

/**
 * Build the request path for one row.
 *
 * `rowId` MUST be percent-encoded: real ids embed `|` (e.g.
 * `144596|107532-82594|2026-08-11`), and Angular's HttpClient does not encode path segments the way
 * it does query parameters — an unencoded pipe would be sent raw.
 */
export const rowDetailPath = (source: RowDetailSource, rowId: string): string =>
  `${ROW_DETAIL_URL}/${source}/${encodeURIComponent(rowId)}`

@Injectable()
export class HttpRowDetailService extends RowDetailService {
  /**
   * Cache keyed on `${source}|${rowId}`, scoped to one panel instance (the service is bound in
   * `Positions2PanelComponent`'s providers, not `providedIn: 'root'`), so two open Positions 2.0
   * panels never share row detail across their independent account sessions.
   */
  private readonly cache = new Map<string, Observable<RowDetailEntry[]>>()

  constructor(private readonly http: HttpClient) {
    super()
  }

  getRowDetail(
    source: RowDetailSource,
    rowId: string,
  ): Observable<RowDetailEntry[]> {
    const key = `${source}|${rowId}`
    const cached = this.cache.get(key)
    if (cached) return cached

    const request = this.http
      .get<RowDetailResponse>(rowDetailPath(source, rowId))
      .pipe(
        // `data: []` is a legitimate 200 for an unknown row, and a defensive `?? []` also keeps a
        // malformed/absent envelope from reaching the view model as `undefined`.
        map(response => response?.data ?? []),
        // On a terminal error, evict this key so the cache never retains a permanently-errored
        // observable — the same rule (and the same reason) as HttpFieldCatalogService. Without it,
        // shareReplay would replay that one failure to every later reader of this row, so
        // re-selecting the row after a transient 500 could never issue a fresh GET.
        catchError((err: unknown) => {
          if (this.cache.get(key) === request) this.cache.delete(key)
          return throwError(() => err)
        }),
        // Replay the resolved detail to every re-subscribe (switching sub-panel tabs back, a
        // re-render) without a refetch, and de-dupe concurrent in-flight requests for the same key.
        shareReplay({ bufferSize: 1, refCount: false }),
      )
    this.cache.set(key, request)
    return request
  }

  invalidate(): void {
    this.cache.clear()
  }
}
